import { DateUtils } from 'src/utils/date'
import CryptoJS from 'crypto-js'
import { positionType } from 'src/pages/CouponMapPage'
import { captureError, SENTRY_SEVERITY } from 'src/utils/sentry'
const { DateFormat, now } = DateUtils

function maskPhoneNumber(phoneNumber: string) {
  const regex = /^\d{11}$/
  if (!regex.test(phoneNumber)) {
    return ''
  }

  const prefix = phoneNumber.slice(0, 3)
  const middle = phoneNumber.slice(3, 7)
  const last = phoneNumber.slice(7)

  const maskedMiddle = middle?.replace(/\d/g, '*')

  return `${prefix}-${maskedMiddle}-${last}`
}
function randomNumber() {
  return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1)
}

const randomPaymentOrderId = (): string =>
  `${randomNumber()}-${randomNumber()}-${DateFormat(now(), 'YYMMDD')}-${randomNumber()}`

const encryptionKey = process.env.REACT_APP_PUMBER_ENCRYPTIONKEY as string

const encryptPhoneNumber = (phoneNumber: string) => {
  const ciphertext = CryptoJS.AES.encrypt(phoneNumber, encryptionKey).toString()
  return encodeURIComponent(ciphertext)
}

const decryptPhoneNumber = (encryptedPhoneNumber: string) => {
  try {
    const decrypt = CryptoJS.AES.decrypt(decodeURIComponent(encryptedPhoneNumber), encryptionKey)
    return (decrypt ? decrypt : '').toString(CryptoJS.enc.Utf8)
  } catch (error) {
    console.warn(error)
    return ''
  }
}

const handleJsonParse = (jsonString: string, defaultData?: string) => {
  try {
    return JSON.parse(jsonString)
  } catch (error) {
    console.warn('JSON parse error:', error)
    captureError('handleJsonParse Error', {
      level: SENTRY_SEVERITY.Info,
      extra: {
        parseError: error instanceof Error ? error.message : String(error),
      },
    })
    return defaultData || jsonString
  }
}

// Haversine 공식을 이용한 거리 계산 함수 (미터 단위로 반환)
function getDistanceFromLatLon(point1: positionType, point2: positionType): string {
  const R = 6371 // 지구 반지름(km)
  const dLat = deg2rad(point2.lat - point1.lat)
  const dLon = deg2rad(point2.lng - point1.lng)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(point1.lat)) *
      Math.cos(deg2rad(point2.lat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distanceKm = R * c // km로 계산된 거리
  // return distanceKm > 1 ? distanceKm.toFixed(2) : `${distanceKm * 1000}m`
  const distanceM = distanceKm * 1000 // m로 변환
  return distanceM > 1000 ? `${(distanceM / 1000).toFixed(1)}km` : `${Math.floor(distanceM)}m`
}

// 도(degree)를 라디안(radian)으로 변환하는 함수
function deg2rad(deg: number): number {
  return deg * (Math.PI / 180)
}

function filterDistance(value: string) {
  return parseInt(
    value
      ?.replace('.', '')
      ?.replace('km', '000')
      ?.replace(/[^0-9]/g, ''),
  )
}

//문자열을 주어진 바이트 수(기본값: 47 바이트)로 자르
function limitStringByBytes(str?: string, maxBytes = 47) {
  if (!!str) {
    let encoder = new TextEncoder()

    if (encoder.encode(str).length <= maxBytes) {
      return str
    }

    let truncatedStr = ''
    for (let i = 0; i < str.length; i++) {
      let tempStr = truncatedStr + str[i]
      let encodedTempStr = encoder.encode(tempStr)
      if (encodedTempStr.length > maxBytes) {
        break
      }
      truncatedStr = tempStr
    }
    return truncatedStr
  } else {
    return ''
  }
}

//쿠폰명에서 브랜드 분리
function getBrandAndCouponName(name?: string) {
  if (!!name) {
    const match = name.match(/^\[(.*?)\]\s*(.*)$/)
    return {
      couponName: match ? match[2] : name,
      brandName: match ? `[${match[1]}]` : '',
    }
  } else {
    return {
      couponName: '',
      brandName: '',
    }
  }
}

//string배열 비교
const areArraysEqual = (arr1: string[], arr2: string[]) => {
  // 두 배열의 길이가 다르면 false
  if (arr1.length !== arr2.length) return false

  // 배열을 정렬하고 각 요소를 비교
  const sortedArr1 = arr1.slice().sort()
  const sortedArr2 = arr2.slice().sort()

  return sortedArr1.every((value, index) => value === sortedArr2[index])
}

const mobileOS = (): 'android' | 'ios' | 'other' => {
  const ua = navigator.userAgent
  if (/android/i.test(ua)) {
    return 'android'
  }
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return 'ios'
  }
  return 'other'
}

const detectDevice = (): 'mobile' | 'tablet' | 'pc' => {
  const ua = navigator.userAgent

  // 명확한 모바일
  if (/android/i.test(ua) || /iPhone|iPod/i.test(ua)) return 'mobile'

  // 터치 지원 + 화면 크기
  if (navigator.maxTouchPoints > 0 && window.innerWidth < 1024) return 'mobile'

  // iPad 특수 케이스
  if (/iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'tablet'
  }

  return 'pc'
}

export {
  maskPhoneNumber,
  filterDistance,
  randomPaymentOrderId,
  encryptPhoneNumber,
  getBrandAndCouponName,
  decryptPhoneNumber,
  handleJsonParse,
  getDistanceFromLatLon,
  limitStringByBytes,
  areArraysEqual,
  mobileOS,
  detectDevice,
}
